import { Divider, Text, useToast, VStack } from "@chakra-ui/react";
import { ApolloError } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  isValidPersonalId,
  normalizeTaxId,
} from "../../../helpers/spanish-tax-id";
import dayjs from "dayjs";
import { isValidPhoneNumber } from "libphonenumber-js";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { countries } from "../../../helpers/countries";
import { Routes } from "../../../helpers/router";

import { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ProfileForm } from "./profile-form";
import { TermsForm } from "./terms-form";

import * as Sentry from "@sentry/react";
import { Button } from "../../../components/chackra-ui-button";
import { useCreateProfileMutation } from "../../../graphql/generated/user-v2-hooks";
import {
  applyFieldValidationIssues,
  parseProfileMutationError,
  PROFILE_DUPLICATE_IDENTITY_ERROR_CODE,
  PROFILE_VALIDATION_ERROR_CODE,
} from "../../../helpers/profile-mutation-errors";
import { useKiosk } from "../../../kiosk/kiosk-context";
import { finishCreatedProfile } from "../../../kiosk/profile-completion";

const validationSchema = (t: TFunction) =>
  Yup.object({
    firstName: Yup.string().required(t("personalDetails.fieldRequired")).trim(),
    lastName: Yup.string().required(t("personalDetails.fieldRequired")).trim(),
    identityNumber: Yup.string()
      .transform((value) =>
        typeof value === "string" ? normalizeTaxId(value) : value,
      )
      .test(
        "dni-nie-valid",
        t("personalDetails.invalidIdentityNumber"),
        function (value) {
          if (!value) return false;
          if (this.parent?.country !== "ES") return true;
          return isValidPersonalId(value);
        },
      )
      .required(t("personalDetails.fieldRequired")),
    dateOfBirth: Yup.string()
      .test("valid", t("personalDetails.invalidDate"), (value) =>
        dayjs(value, "YYYY-MM-DD", true).isValid(),
      )
      .test("min-age", t("personalDetails.minAge"), (value) => {
        if (!value) return false;
        const isValid = dayjs(value, "YYYY-MM-DD", true).isValid();
        if (!isValid) return false;
        return dayjs().diff(value, "years") >= 14;
      })
      .required(t("personalDetails.fieldRequired")),
    country: Yup.string()
      .required(t("personalDetails.fieldRequired"))
      .oneOf(
        countries.map((country) => country.code),
        t("personalDetails.fieldRequired"),
      ),
    phoneNumber: Yup.string()
      .trim()
      .required(t("personalDetails.fieldRequired"))
      .test("valid", t("personalDetails.invalidPhoneNumber"), (value) =>
        value ? isValidPhoneNumber(value) : false,
      ),
    // supervisorFirstName: Yup.string()
    //   .trim()
    //   .when("dateOfBirth", {
    //     is: (value: string) => dayjs().diff(value, "years") < 18,
    //     then: (schema) =>
    //       schema.required(t("personalDetails.form.fieldRequired")),
    //   }),
    // supervisorLastName: Yup.string()
    //   .trim()
    //   .when("dateOfBirth", {
    //     is: (value: string) => dayjs().diff(value, "years") < 18,
    //     then: (schema) =>
    //       schema.required(t("personalDetails.form.fieldRequired")),
    //   }),
    // supervisorPhoneNumber: Yup.string()
    //   .trim()
    //   .when("dateOfBirth", {
    //     is: (value: string) => dayjs().diff(value, "years") < 18,
    //     then: (schema) =>
    //       schema
    //         .test(
    //           "valid",
    //           t("personalDetails.form.invalidPhoneNumber"),
    //           (value) => (value ? isValidPhoneNumber(value) : false)
    //         )
    //         .required(t("personalDetails.form.fieldRequired")),
    //   }),
    // supervisorEmail: Yup.string()
    //   .trim()
    //   .when("dateOfBirth", {
    //     is: (value: string) => dayjs().diff(value, "years") < 18,
    //     then: (schema) =>
    //       schema.required(t("personalDetails.form.fieldRequired")),
    //   }),
    healthTerms: Yup.boolean()
      .required()
      .oneOf([true], t("personalDetails.mustAcceptTerms")),
    underageTerms: Yup.boolean().when("dateOfBirth", {
      is: (value: string) => dayjs().diff(value, "years") >= 18,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .required(t("personalDetails.mustAcceptTerms"))
          .oneOf([true], t("personalDetails.mustAcceptTerms")),
    }),
  });

export type PersonalDetailsFormValues = {
  firstName: string;
  lastName: string;
  identityNumber: string;
  dateOfBirth: string;
  country: string;
  phoneNumber: string;
  // supervisorFirstName?: string;
  // supervisorLastName?: string;
  // supervisorPhoneNumber?: string;
  // supervisorEmail?: string;
  healthTerms: boolean;
  underageTerms?: boolean;
};

const profileFormFieldKeys: Array<keyof PersonalDetailsFormValues> = [
  "firstName",
  "lastName",
  "identityNumber",
  "dateOfBirth",
  "country",
  "phoneNumber",
];

export const OnboardingPersonalDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isKiosk, completeProfileCreation } = useKiosk();
  // const planId = searchParams.get("plan_id");
  const toast = useToast();
  const methods = useForm<PersonalDetailsFormValues>({
    resolver: yupResolver(validationSchema(t)),
    reValidateMode: "onChange",
    defaultValues: {
      country: "ES",
    },
  });

  const showErrorToast = (message: string) => {
    toast({
      title: message,
      status: "error",
      isClosable: true,
    });
  };

  const handleCreateProfileMutationError = (error: ApolloError) => {
    const parsedError = parseProfileMutationError(error);
    if (!parsedError) {
      showErrorToast(error.message || t("genericError"));
      Sentry.captureException(error);
      return;
    }

    if (parsedError.code === PROFILE_VALIDATION_ERROR_CODE) {
      if (parsedError.fieldIssues.length === 0) {
        showErrorToast(parsedError.message || t("genericError"));
        return;
      }

      const unknownFieldMessages = applyFieldValidationIssues(
        methods.setError,
        profileFormFieldKeys,
        parsedError.fieldIssues,
      );

      if (unknownFieldMessages.length > 0) {
        showErrorToast(unknownFieldMessages[0]);
      }
      return;
    }

    if (parsedError.code === PROFILE_DUPLICATE_IDENTITY_ERROR_CODE) {
      showErrorToast(parsedError.message || t("genericError"));
      return;
    }

    showErrorToast(parsedError.message || t("genericError"));
    Sentry.captureException(error, {
      extra: { parsedError },
    });
  };

  const [createProfile] = useCreateProfileMutation({
    context: { endpoint: "fitnextv2" },
    onError: handleCreateProfileMutationError,
    onCompleted: (data) => {
      if (data?.profileId) {
        finishCreatedProfile({
          isKiosk,
          completeKiosk: completeProfileCreation,
          navigateDashboard: () =>
            navigate({
              pathname: Routes.Dashboard(),
              search: searchParams.toString(),
            }),
        });
        return;
      }

      const error = new Error("createProfile completed without profileId");
      showErrorToast(t("genericError"));
      Sentry.captureException(error, {
        extra: { data },
      });
    },
  });

  const onSubmit = methods.handleSubmit(
    async (values) => {
      methods.clearErrors(profileFormFieldKeys);
      await createProfile({
        variables: {
          input: {
            country: values.country,
            firstName: values.firstName,
            lastName: values.lastName,
            dateOfBirth: values.dateOfBirth,
            phoneNumber: values.phoneNumber,
            identityNumber: values.identityNumber,
          },
        },
      });
    },
    (errors, event) => {
      Sentry.captureException(
        new Error("Unable to submit personal details form"),
        {
          extra: {
            event,
            errors,
          },
        },
      );
    },
  );

  const dateOfBirth = methods.watch("dateOfBirth");

  const isUnderage =
    !!dateOfBirth &&
    dayjs(dateOfBirth, "YYYY-MM-DD", true).isValid() &&
    dayjs().diff(dateOfBirth, "years") < 18;

  // TODO: Bring back when plan are enabled
  // if (!planId) {
  //   return <Navigate to={Router.Onboarding.Plan} replace />;
  // }

  // if (data?.profiles && data.profiles.length > 0) {
  //   return <Navigate to={Router.Dashboard} replace />;
  // }

  return (
    <VStack w="full" flex={1} justify="center" spacing={0}>
      <VStack w="full" maxW="680px" spacing={0} mx="auto" px={4}>
        <Text
          fontFamily="'Barlow Condensed', sans-serif"
          fontSize="28px"
          fontWeight={700}
          color="#13192E"
          textAlign="center"
          mb="6px"
        >
          {t("personalDetails.title")}
        </Text>
        <Text
          fontSize="16px"
          color="slate.500"
          textAlign="center"
          mb={6}
        >
          {t("personalDetails.subtitle")}
        </Text>
        <FormProvider {...methods}>
          <form style={{ width: "100%" }} onSubmit={onSubmit} noValidate autoComplete="off">
            <VStack spacing={5} w="full">
              <ProfileForm />
              <Divider borderColor="slate.200" />
              <TermsForm showUnderageTerms={isUnderage} />
              <Button
                isLoading={methods.formState.isSubmitting}
                type="submit"
                variant="senseiFilled"
                w="full"
                mt={2}
              >
                {t("personalDetails.nextButton")}
              </Button>
            </VStack>
          </form>
        </FormProvider>
      </VStack>
    </VStack>
  );
};
